<template>
  <bb-modal visible @close="onClose()" flavor="card" color="white" publicSize="m" fullMobile class="cancel-application">
    <template v-if="isLoading">
      <div class="cancel-application__loader">
        <bb-spinner/>
      </div>
    </template>
    <template v-else>
      <div slot="heading" class="cancel-application__title">{{ translations.modalTitle }}</div>
      <div class="cancel-application__content">
        <div class="cancel-application__subtitle">
          <div class="cancel-application__description">{{ translations.modalText }}</div>
          <bb-input
            :value="depositAmount"
            :label="translations.depositAmountLabel"
            data-testid="cancel-application-deposit-amount"
            disabled
            name="deposit-amount"
            framed
            :add-on="currencySymbol"
            class="m-b-10"
          />
          <bb-input
            :value="applicationInterestRate"
            :label="translations.interestRateLabel"
            data-testid="cancel-application-interest-rate"
            disabled
            name="interest-rate"
            framed
            currency
            add-on="%"
            class="m-b-10"
          />
          <bb-input
            v-if="cancelApplicationDetails.depositPeriod"
            :value="applicationDepositPeriod"
            :label="translations.depositPeriodLabel"
            data-testid="cancel-application-deposit-period"
            disabled
            name="deposit-period"
            framed
            currency
            :add-on="cancelApplicationDetails.depositPeriodCode"
            class="m-b-10"
          />
          <bb-input
            :value="interestOutpaymentFrequency"
            :label="translations.interestOutpaymentFrequencyLabel"
            data-testid="cancel-application-interest-outpayment-frequency"
            disabled
            name="interest-outpayment-frequency"
            framed
            class="m-b-10"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="cancel-deposit-button">
        <bb-button
          data-testid="cancel-application-close"
          :disabled="isLoading"
          display="block"
          color="red"
          @click="onCancelDepositApplication">
          {{ translations.cancel }}
        </bb-button>
      </div>
    </template>
  </bb-modal>
</template>

<script>
import { api } from '@/modules/deposits/api'
import { formatMoneyWithCurrency } from '@/plugins/numformat'
import { mapActions, mapState } from 'pinia'
import { useRootStore } from '@/store/root'
import { currencyCodeToSymbol } from '@/plugins/currencyFormatters'
import { useDepositStore } from '@deposits/store/depositStore'
import preferenceMixin from '@/mixins/preferenceMixin'

export default {
  name: 'cancel-deposit-application-modal',
  mixins: [preferenceMixin],
  props: {
    cancelApplicationDetails: {
      required: true
    }
  },
  data () {
    return {
      isLoading: false
    }
  },
  computed: {
    ...mapState(useRootStore, ['locale']),
    secondaryDepositAmount () {
      return this.getSeparatedSecondaryAmount(this.cancelApplicationDetails.depositAmount, this.cancelApplicationDetails.currencyCode)
    },
    depositAmount () {
      const formattedPrimaryAmount = formatMoneyWithCurrency(
        this.cancelApplicationDetails.depositAmount,
        null,
        this.locale
      )

      return !this.secondaryDepositAmount
        ? formattedPrimaryAmount
        : `${formattedPrimaryAmount} / ${this.secondaryDepositAmount.formattedValue}`
    },
    currencySymbol () {
      const primaryCurrencySymbol = currencyCodeToSymbol(this.cancelApplicationDetails.currencyCode)

      return !this.secondaryDepositAmount
        ? primaryCurrencySymbol
        : `${primaryCurrencySymbol} / ${currencyCodeToSymbol(this.secondaryDepositAmount.currencyCode)}`
    },
    interestOutpaymentFrequency () {
      return this.translations.interestOutpaymentFrequency[this.cancelApplicationDetails.interestOutpaymentFrequencyCode]
    },
    translations () {
      return {
        modalTitle: this.$pgettext('close_pending_deposit', 'Cancel deposit application'),
        modalText: this.$pgettext('close_pending_deposit', "You can't undo this action. Are you sure you want to cancel the application?"),
        depositAmountLabel: this.$pgettext('close_pending_deposit', 'Deposit amount'),
        interestRateLabel: this.$pgettext('close_pending_deposit', 'Interest rate'),
        depositPeriodLabel: this.$pgettext('close_pending_deposit', 'Deposit period'),
        interestOutpaymentFrequencyLabel: this.$pgettext('close_pending_deposit', 'Interest outpayment frequency'),
        cancel: this.$pgettext('close_pending_deposit', 'Yes, cancel deposit application'),
        interestOutpaymentFrequency: {
          END: this.$pgettext('close_pending_deposit', 'At the end'),
          YEAR: this.$pgettext('close_pending_deposit', 'Once a year'),
          MONTH: this.$pgettext('close_pending_deposit', 'Once a month')
        }
      }
    },
    applicationInterestRate () {
      return this.cancelApplicationDetails.interestRate
    },
    applicationDepositPeriod () {
      return this.cancelApplicationDetails.depositPeriod
    }
  },
  methods: {
    ...mapActions(useDepositStore, ['getDeposits']),
    onClose () {
      this.$emit('close')
    },
    async onCancelDepositApplication () {
      this.isLoading = true

      try {
        await api.cancelDepositApplication(this.cancelApplicationDetails.contractVersionId)
        await this.getDeposits()
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
        this.$emit('close')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@media (max-width: 768px) {
  .cancel-deposit-button {
    padding-left: 30px !important;
    padding-right: 30px !important;
    margin-bottom: 30px !important;
  }
}

.cancel-application {
  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    color: $blue;
    font-size: $font-size-small;
    font-family: $gotham-medium;
    text-align: center;
  }

  &__subtitle {
    color: $gray;
    text-align: center;
    margin-top: 8px;
    margin-bottom: 15px;
  }

  &__details-box {
    margin-top: 30px;
  }

  &__amount {
    margin-top: 15px;
    text-align: center;
    font-family: $gotham-medium;
    font-size: $h3-size;
    line-height: $h3-size;
  }

  &__amount-subtitle {
    color: $gray-70;
    font-family: $gotham-bold;
    text-align: center;
    font-size: $font-size-tiny;
    text-transform: uppercase;
    padding-bottom: 15px;
  }

  &__description {
    color: $gray;
    text-align: center;
    font-size: 14px;
    margin-bottom: 5px;

    @media (min-width: $desktop-view-breaking-point) {
      font-size: 16px;
    }
  }
}
</style>
